import { defineStore } from 'pinia'
import { getCurrentPath } from '@/utils/common'

/**
 * 全局确认弹窗（pinia store + 布局里一个 `<GlobalDialog />`）。
 *
 * 用它的原因：`wot` 的 `useMessage()` 依赖组件上下文（inject），`http.ts` 这类工具模块拿不到实例，
 * 只能用 `uni.showModal` —— 那是原生白框，和项目主题不一致。store 不依赖上下文，
 * 网络层、工具函数都能直接调用，UI 也统一走 wot 组件。
 *
 * 与 `useGlobalToast` 同一套路：记录 `currentPage`，弹窗只在发起的页面渲染，切页不串。
 */
export interface GlobalDialogOptions {
  /** 标题，缺省「提示」 */
  title?: string
  /** 正文 */
  content?: string
  /** 确定按钮文案，缺省「确定」 */
  confirmText?: string
  /** 取消按钮文案，缺省「取消」 */
  cancelText?: string
  /** 是否展示取消按钮，缺省展示 */
  showCancel?: boolean
  /** 点确定后的回调（弹窗先关闭再执行） */
  onConfirm?: () => void
  /** 点取消 / 关闭后的回调 */
  onCancel?: () => void
}

interface GlobalDialogState {
  dialogOptions: (GlobalDialogOptions & { currentPage: string }) | null
}

export const useGlobalDialog = defineStore('global-dialog', {
  state: (): GlobalDialogState => ({
    dialogOptions: null,
  }),
  actions: {
    show(option: GlobalDialogOptions) {
      this.dialogOptions = { ...option, currentPage: getCurrentPath() }
    },
    /** 只有一个按钮的提示 */
    alert(option: GlobalDialogOptions) {
      this.show({ ...option, showCancel: false })
    },
    /** 确认 / 取消两个按钮 */
    confirm(option: GlobalDialogOptions) {
      this.show({ ...option, showCancel: option.showCancel !== false })
    },
    close() {
      this.dialogOptions = null
    },
  },
})
